import { useState } from "react";
import { Util } from "../util/Util";
import { resolveStoredDateRange } from "../util/sessionFilters";
import { computePresetDateRange } from "../util/dateRangePresets";
import { businessTodayDate } from "../util/businessClock";
import i18n from "../i18n";

export interface FilterDateRange {
  from: Date | null;
  to: Date | null;
}

export type PeriodQueryParams = {
  From: string | null;
  To: string | null;
};

export const isActiveDateRange = (
  range: FilterDateRange,
): range is { from: Date; to: Date } => range.from !== null && range.to !== null;

/** Always returns From/To keys; null values are submitted explicitly to the API. */
export const getPeriodQueryParams = (range: FilterDateRange): PeriodQueryParams => ({
  From: range.from ? Util.formatISODate(range.from) : null,
  To: range.to ? Util.formatISODate(range.to) : null,
});

export const periodRequestConfig = (
  range: FilterDateRange,
): { params: PeriodQueryParams } => ({
  params: getPeriodQueryParams(range),
});

export const formatPeriodLabel = (range: FilterDateRange): string | undefined => {
  if (range.from === null && range.to === null) return undefined;
  if (!isActiveDateRange(range)) return undefined;
  return formatPeriodDisplay(range, i18n.language);
};

const formatPeriodDate = (date: Date, locale?: string) =>
  date.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });

/** Human-readable period for headers and chips. */
export const formatPeriodDisplay = (range: FilterDateRange, locale?: string): string => {
  if (range.from === null && range.to === null) return '';
  if (range.from && range.to) {
    return `${formatPeriodDate(range.from, locale)} – ${formatPeriodDate(range.to, locale)}`;
  }
  if (range.from) return `${formatPeriodDate(range.from, locale)} →`;
  if (range.to) return `→ ${formatPeriodDate(range.to, locale)}`;
  return '—';
};

/** Same range (by time), so a filter re-announcing its value does not re-render or re-trigger effects. */
export const sameDateRange = (a: FilterDateRange, b: FilterDateRange) =>
  (a.from?.getTime() ?? null) === (b.from?.getTime() ?? null) && (a.to?.getTime() ?? null) === (b.to?.getTime() ?? null);

// Hook
/**
 * `storageKey`: the page's key (the one its DateRangeFilter stores under), so
 * the range starts from the choice kept for this session instead of today.
 */
export const useDateRange = (storageKey?: string) => {
  // The default is the "today" preset itself, so a DateRangeFilter showing
  // Today and the page asking for today agree from the first render.
  const [dateRange, setDateRange] = useState<FilterDateRange>(
    () => resolveStoredDateRange(storageKey) ?? computePresetDateRange("today", businessTodayDate())!,
  );

  return {
    dateRange,
    setDateRange,
  };
};